import {
  getAiTargetBounds,
  formatBloodPressureTargetLabel,
  formatBloodGlucoseTargetLabel,
  formatWeightTargetLabel,
  getPrimaryBloodPressureTarget,
  getPrimaryBloodGlucoseTarget
} from './scheduleTargetUtils';

/** A stored reading, including 0. Blank and negative values are not readings. */
export function readRecordedNumber(...values) {
  for (const value of values) {
    if (value === null || value === undefined || value === '' || value === 'N/A') continue;
    const n = Number(value);
    if (Number.isFinite(n) && n >= 0) return n;
  }
  return null;
}

export const isPlausibleSys = (v) => readRecordedNumber(v) != null;
export const isPlausibleDia = (v) => readRecordedNumber(v) != null;
export const isPlausiblePulse = (v) => readRecordedNumber(v) != null;

const avg = (arr) => {
  if (!arr?.length) return null;
  return Math.round(arr.reduce((a, b) => a + b, 0) / arr.length);
};

const min = (arr) => (arr?.length ? Math.min(...arr) : null);
const max = (arr) => (arr?.length ? Math.max(...arr) : null);

function pct(count, total) {
  if (!total) return '0% (0)';
  return `${Math.round((count / total) * 100)}% (${count || 0})`;
}

function buildSeries(measurements = [], kind) {
  return measurements.slice(0, 250).map((m) => {
    const t = m.measure_new_date_time || m.measure_date_time || m.created_at || m.measurement_date || '';
    if (kind === 'glucose') {
      return {
        t,
        glucose: Number(m.blood_glucose_value_1 ?? m.blood_glucose_value ?? m.glucose_value ?? m.glucose)
      };
    }
    if (kind === 'weight') {
      return { t, weight: Number(m.weight_lbs ?? m.weight) };
    }
    return {
      t,
      sys: Number(m.systolic_pressure ?? m.systolic),
      dia: Number(m.diastolic_pressure ?? m.diastolic),
      pulse: m.pulse != null ? Number(m.pulse) : null
    };
  });
}

function pickLatestReading(measurements = [], kind) {
  if (!measurements.length) return null;
  const sorted = [...measurements].sort((a, b) => {
    const da = new Date(a.measure_new_date_time || a.measure_date_time || a.created_at || 0).getTime();
    const db = new Date(b.measure_new_date_time || b.measure_date_time || b.created_at || 0).getTime();
    return db - da;
  });
  if (kind === 'glucose') {
    const m = sorted.find((row) => {
      const value = Number(row.blood_glucose_value_1 ?? row.blood_glucose_value ?? row.glucose_value ?? row.glucose);
      return Number.isFinite(value);
    });
    if (!m) return null;
    const value = Number(m.blood_glucose_value_1 ?? m.blood_glucose_value ?? m.glucose_value ?? m.glucose);
    return { glucose: value };
  }
  if (kind === 'weight') {
    const m = sorted.find((row) => Number.isFinite(Number(row.weight_lbs ?? row.weight)));
    if (!m) return null;
    const weight = Number(m.weight_lbs ?? m.weight);
    return { weight, bmi: m.bmi != null ? Number(m.bmi) : null };
  }
  const m = sorted.find((row) => (
    readRecordedNumber(row.systolic_pressure, row.systolic) != null
    || readRecordedNumber(row.diastolic_pressure, row.diastolic) != null
  ));
  if (!m) return null;
  return {
    systolic: readRecordedNumber(m.systolic_pressure, m.systolic),
    diastolic: readRecordedNumber(m.diastolic_pressure, m.diastolic),
    pulse: readRecordedNumber(m.pulse)
  };
}

/**
 * Compact stats from the RPM Overview Summary of Measurement — this is what the AI narrates.
 */
export function buildMeasurementAiSummaryPayload({
  vitalType,
  periodLabel,
  measurements = [],
  summaryPeriodData,
  activeSummaryRows = [],
  scheduleTargets = null
}) {
  const kind = summaryPeriodData?.kind || 'bp';
  const overallRow = activeSummaryRows[0] || null;
  const bounds = getAiTargetBounds(scheduleTargets);
  const bpLabel = formatBloodPressureTargetLabel(getPrimaryBloodPressureTarget(scheduleTargets));
  const bgLabel = formatBloodGlucoseTargetLabel(getPrimaryBloodGlucoseTarget(scheduleTargets));
  const weightLabel = formatWeightTargetLabel(scheduleTargets?.weightTargetRange);
  const periods = (summaryPeriodData?.periodList || [])
    .map((name) => {
      const stats = summaryPeriodData.periodStats?.[name];
      if (!stats) return null;
      if (kind === 'glucose') {
        if (!stats.count) return null;
        return {
          period: name,
          avg: avg(stats.vals),
          max: max(stats.vals),
          min: min(stats.vals),
          count: stats.count,
          above: stats.above,
          within: stats.within,
          below: stats.below
        };
      }
      if (kind === 'weight') {
        if (!stats.count) return null;
        return {
          period: name,
          weightAvg: avg(stats.weightVals),
          weightMax: max(stats.weightVals),
          weightMin: min(stats.weightVals),
          count: stats.count,
          above: stats.above,
          within: stats.within,
          below: stats.below
        };
      }
      const sysVals = (stats.sysVals || []).filter(isPlausibleSys);
      const diaVals = (stats.diaVals || []).filter(isPlausibleDia);
      const pulseVals = (stats.pulseVals || []).filter(isPlausiblePulse);
      const count = sysVals.length || diaVals.length;
      if (!count) return null;
      return {
        period: name,
        count,
        systolicAvg: avg(sysVals),
        systolicMax: max(sysVals),
        systolicMin: min(sysVals),
        diastolicAvg: avg(diaVals),
        diastolicMax: max(diaVals),
        diastolicMin: min(diaVals),
        pulseAvg: avg(pulseVals),
        above: stats.above,
        within: stats.within,
        below: stats.below
      };
    })
    .filter(Boolean);

  const totals = summaryPeriodData?.totals || {};
  let targetMix = null;
  let overall = null;

  if (kind === 'glucose') {
    targetMix = {
      above: pct(totals.above, totals.total),
      within: pct(totals.within, totals.total),
      below: pct(totals.below, totals.total)
    };
    overall = overallRow
      ? {
          primaryAvg: overallRow.primaryAvg ?? null,
          primaryMax: overallRow.primaryMax ?? null,
          primaryMin: overallRow.primaryMin ?? null,
          targetRange: overallRow.targetRange || bgLabel
        }
      : null;
  } else if (kind === 'weight') {
    targetMix = {
      weightAbove: pct(totals.aboveWeight, totals.totalWeight),
      weightWithin: pct(totals.withinWeight, totals.totalWeight),
      weightBelow: pct(totals.belowWeight, totals.totalWeight)
    };
    overall = overallRow
      ? {
          primaryAvg: overallRow.primaryAvg ?? null,
          primaryMax: overallRow.primaryMax ?? null,
          primaryMin: overallRow.primaryMin ?? null,
          targetRange: overallRow.targetRange || weightLabel
        }
      : null;
  } else {
    targetMix = {
      systolicAbove: pct(totals.above?.sys, totals.sysTotal),
      systolicWithin: pct(totals.within?.sys, totals.sysTotal),
      systolicBelow: pct(totals.below?.sys, totals.sysTotal),
      diastolicAbove: pct(totals.above?.dia, totals.diaTotal),
      diastolicWithin: pct(totals.within?.dia, totals.diaTotal),
      diastolicBelow: pct(totals.below?.dia, totals.diaTotal)
    };
    const sysVals = measurements
      .map((m) => readRecordedNumber(m.systolic_pressure, m.systolic))
      .filter((v) => v != null);
    const diaVals = measurements
      .map((m) => readRecordedNumber(m.diastolic_pressure, m.diastolic))
      .filter((v) => v != null);
    const pulseVals = measurements
      .map((m) => readRecordedNumber(m.pulse))
      .filter((v) => v != null);
    overall = {
      systolicAvg: avg(sysVals),
      diastolicAvg: avg(diaVals),
      pulseAvg: avg(pulseVals),
      systolicMin: min(sysVals),
      systolicMax: max(sysVals),
      diastolicMin: min(diaVals),
      diastolicMax: max(diaVals),
      primaryAvg: avg(sysVals),
      targetRange: overallRow?.targetRange || bpLabel
    };
  }

  const readingCount = kind === 'bp'
    ? measurements.filter((m) => (
      readRecordedNumber(m.systolic_pressure, m.systolic) != null
      || readRecordedNumber(m.diastolic_pressure, m.diastolic) != null
    )).length
    : measurements.length;

  const targets = kind === 'glucose'
    ? { glucoseMin: bounds.glucoseMin, glucoseMax: bounds.glucoseMax }
    : kind === 'weight'
      ? { weightMin: bounds.weightMin, weightMax: bounds.weightMax }
      : {
          systolicMin: bounds.systolicMin,
          systolicMax: bounds.systolicMax,
          diastolicMin: bounds.diastolicMin,
          diastolicMax: bounds.diastolicMax,
          pulseMin: bounds.pulseMin,
          pulseMax: bounds.pulseMax
        };

  return {
    vitalType,
    periodLabel,
    readingCount,
    latest: pickLatestReading(measurements, kind),
    overall,
    totals: targetMix,
    periods,
    series: buildSeries(measurements, kind),
    targets
  };
}
